import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";

import { GridOptions, RedrawRowsParams, ColDef, GridApi } from "ag-grid-community";

import { RouterLinkRendererComponent } from "../../ag-grid/router-link-renderer.component";
import { SubHeader } from "../../../../core/models/sub-header.model";
import { OrganizationService } from "../../../services/organization.service";
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { Subscription, forkJoin, of } from "rxjs";
import { tap, switchMap } from "rxjs/operators";
import { OrganizationComplianceDetails, FinancialComplianceSearchSpec, Organization, OperationType } from "../../../models/organization.model";
import { AgGridBooleanFilterComponent, IBooleanFilterParams } from "../../ag-grid/filters/boolean-filter.component";
import { CheckboxRendererComponent } from "../../ag-grid/checkbox-renderer.component";
import { GridFormatterService } from "../../../services/grid-formatter.service";

@Component({
    selector: 'org-list',
    templateUrl: './organization-list.component.html',
    standalone: false
})
export class OrganizationListComponent implements OnInit {
    private complianceDetails: OrganizationComplianceDetails[] = [];

    columnActive = {
        headerClass: "wrapped",
        headerName: 'Active',
        field: 'isActive',
        tooltipValueGetter: this.activeFormatter(this),
        filter: AgGridBooleanFilterComponent,
        filterParams: { isInServerSideFilterMode: false } as IBooleanFilterParams,
        minWidth: 115,
        maxWidth: 125,
        cellRenderer: CheckboxRendererComponent,
                cellRendererParams: {
                    action: () => { return false; },
                    field: "isActive",
                    description: "Active",
                    isVisible: () => { return true; },
                    isEnabled: () => { return false; },
                    renderAsYesNo: true
            },
    };

    columnRegistered = {
        headerClass: "wrapped",
        headerName: 'Registered',
        field: 'hasRegistered',
        tooltipValueGetter: this.registeredFormatter(this),
        filter: AgGridBooleanFilterComponent,
        filterParams: { isInServerSideFilterMode: false } as IBooleanFilterParams,
        minWidth: 150,
        maxWidth: 155,
        cellRenderer: CheckboxRendererComponent,
                cellRendererParams: {
                    action: () => { return false; },
                    field: "hasRegistered",
                    description: "Registered",
                    isVisible: () => { return true; },
                    isEnabled: () => { return false; },
                    renderAsYesNo: true
            },
    };

    columnLegacyOwnerNumber = {
        headerClass: "wrapped",
        headerName: 'Legacy Owner Number',
        headerTooltip: "Legacy Owner Number",
        tooltipField: "historicalOwnerNumber",
        field: 'historicalOwnerNumber',
        filter: "agTextColumnFilter",
        minWidth: 130,
        maxWidth: 130
        };

    columnName = {
        headerClass: "wrapped",
        headerName: 'Organization Name',
        headerTooltip: "Organization Name",
        tooltipField: "name",
        field: 'name',
        filter: "agTextColumnFilter",
        cellRenderer: RouterLinkRendererComponent,
        cellRendererParams: {
            textField: 'name',
            openInNewWindow: true,
            url: (data: Organization) => { return `/common/organization/${data.id}`}
        },
        minWidth: 155,
        maxWidth: 185
    };

    columnOwnerNumber = {
        headerClass: "wrapped",
        headerName: 'Owner Number',
        headerTooltip: "Owner Number",
        tooltipField: "id",
        field: 'id',
        filter: "agTextColumnFilter",
        minWidth: 125,
        maxWidth: 175
    };

    columnStatus = {
        headerClass: "wrapped",
        headerName: 'Compliance Status',
        headerTooltip: "Compliance Status",
        tooltipField: "complianceStatus",
        field: 'complianceStatus',
        valueGetter: this.complianceStatusText(this),
        filter: 'agSetColumnFilter',
        minWidth: 100,
        maxWidth: 175
    };

    columnType = {
        headerClass: "wrapped",
        headerName: 'Business Entity Type',
        headerTooltip: "Business Entity Type",
        tooltipValueGetter: this.typeFormatter(this),
        field: 'typeCode',
        valueGetter: this.typeFormatter(this),
        filter: 'agSetColumnFilter',
        minWidth: 110,
        maxWidth: 200
    };

    columnOwnerType: ColDef = {
        headerClass: "wrapped",
        headerName: "Owner Type",
        headerTooltip: "Owner Type",
        tooltipValueGetter: this.formatOwnerType(),
        valueGetter: this.formatOwnerType(),
        minWidth: 115,
        maxWidth: 150
    } as ColDef;

    gridColumns = [
        this.columnName,
        this.columnOwnerNumber,
        this.columnLegacyOwnerNumber,
        this.columnRegistered,
        this.columnActive,
        this.columnStatus,
        this.columnType,
        this.columnOwnerType
       
    ];
    data: any;
	gridOptions: GridOptions
	gridApi?: GridApi;
    subscriptions: Subscription = new Subscription();
    orgCompliances: OrganizationComplianceDetails[];

    statusBar = {
        statusPanels: [
            {
                statusPanel: 'agTotalAndFilteredRowCountComponent',
                align: 'right',
            }
        ]
    };


    subHeader: SubHeader = new SubHeader({
        title: "Manage External Organizations",
        buttons: []
    });
     
    

    constructor(public service: OrganizationService,
        public subHeaderService: SubHeaderService,
        private formatter: GridFormatterService,
        public router: Router) {
        this.formatter.applyFilterKeyboardHintTooltips(this.gridColumns);
    }

    ngOnInit() {

        this.gridOptions = <GridOptions>{
            columnDefs: this.gridColumns,
            defaultColDef: { filter: true, sortable: true } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
            getRowClass: (params: any) => {
                if (!this.complianceStatus(this, params.data)) return 'red';
            },
            autoSizeStrategy: { type: 'fitGridWidth' },
            headerHeight: 50,
            onFilterChanged: () => {
                var params = <RedrawRowsParams>{};
                if (this.gridApi != null) {
                    this.gridApi.redrawRows(params);
                }
            },
			onGridReady: (params) => {
				this.gridApi = params.api;
            },
            pagination: false,
            //paginationAutoPageSize: true,
        };
        this.subHeaderService.updateSubHeader(this.subHeader);
        let complianceObs$ = this.service.getComplianceData({ organizationIds: [], includeHistoricOwnerNumbers: false } as FinancialComplianceSearchSpec);
        let orgObs$ = this.service.getAllExternalOrganizations();

        let dataObs$ = forkJoin([complianceObs$, orgObs$]).pipe(
            tap(([compliances, organizations]) => {
                this.complianceDetails = compliances;
                this.data = organizations;
            })
        ).subscribe({
            next: () => { },
            error: (error) => { console.error(error); }
        })

        this.subscriptions.add(dataObs$);
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    activeFormatter(component: any) {
        return (obj: any) => this.getBooleanToYesOrNo(obj.data.isActive);
    }

    getBooleanToYesOrNo(bit: boolean): string {
        return bit ? 'Yes' : 'No';
    }

    getComplianceStatusDescription(status: number): string {
        return status ? 'Out of Compliance' : 'Compliant';
    }

    getTypeDescription(code: string): string {
        return this.service.getTypeDescription(code);
    }

    registeredFormatter(component: any) {
        return (obj: any) => this.getBooleanToYesOrNo(obj.data.hasRegistered);
    }

    showDetail() {
        return (obj: any) => {
            const id = obj.data.id;
            this.router.navigate(['/common/organization', id]);
        }
    }

    statusFormatter(component: any) {
        return (obj: any) => component.getComplianceStatusDescription(obj.data.complianceStatus);
    }

    typeFormatter(component: any) {
        return (obj: any) => component.getTypeDescription(obj.data.typeCode)
    }

    complianceStatus(component: any, obj: any): boolean {
        let complianceStatus: boolean = false;
            if (this.complianceDetails != null && this.complianceDetails.length > 0) {
                let compliance = this.complianceDetails.find(d => d.orgId == obj.id);
                complianceStatus = compliance != null && compliance.isBondingCompliant && compliance.isInsuranceCompliant;
            }
        return complianceStatus;
    }

    complianceStatusText(component: any) {
        return (obj: any) => {
            let response = "Not on File";
            let compliance = this.complianceStatus(component, obj.data);
            if (compliance != null) response = compliance ? 'Compliant' : 'Out of Compliance';
            return response;
        }
    }

    formatOwnerType(): any {
        return (obj: any) => {
            let returnValue: string = "";
            if (obj.data != null && obj.data["operationType"] != null) {
                let isOperatorFunc = this.formatter.flagEnumFormatter("operationType", OperationType, "Operator", true);
                let isInjectionOperatorFunc = this.formatter.flagEnumFormatter("operationType", OperationType, "InjectionOperator", true);
                let isBrineHaulerFunc = this.formatter.flagEnumFormatter("operationType", OperationType, "BrineHauler", true);
                let isBrineHauler = isBrineHaulerFunc(obj);
                let isOperator = isOperatorFunc(obj);
                let isInjectionOperator = isInjectionOperatorFunc(obj);
                if (isBrineHauler == true) {
                    returnValue = "Brine Hauler";
                }
                if (isOperator == true || isInjectionOperator == true) {
                    if (returnValue.length > 0) returnValue += ", ";
                    returnValue += "Well Owner";
                }
            }
            if (returnValue.length === 0) returnValue = "N/A";
            return returnValue;
        }
    }
}
